import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Link,
} from "@chakra-ui/react";
import { CreateCommitmentClassModal } from "features/commitment-classes/CreateCommitmentClassModal";
import { useInvestorClassInformationListSuspense } from "gen/endpoints/investor-class-information/investor-class-information";
import { GenericTableData } from "gen/model/genericTableData";
import { GenericTableRow } from "gen/model/genericTableRow";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForRowFn } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { PlusCircleIcon } from "shared/icons/PlusCircleIcon";
import { rootRoute } from "shared/navigation/Routes";
import { useBoolean } from "usehooks-ts";

export const CommitmentClassesPage: React.FC = () => {
  const { showToast } = useToast();
  const { t } = useTranslation(["commitmentClass", "common"]);
  const { fundFamilyId } = useRequiredStringParams();

  const {
    value: isAddDialogOpen,
    setTrue: openAddDialog,
    setFalse: closeAddDialog,
  } = useBoolean();

  const { data, refetch } =
    useInvestorClassInformationListSuspense(fundFamilyId);

  const tableData = useMemo(
    (): GenericTableData => ({
      columns: [
        {
          id: "name_group",
          is_header: true,
          columns: [
            {
              id: "commitment_class_name",
              title: t("commitmentClass:class"),
            },
          ],
        },
        {
          id: "commitment_class_custom_fee",
          title: t("common:custom.fee"),
        },
        {
          id: "commitment_class_custom_waterfall",
          title: t("common:custom.waterfall"),
        },
        {
          id: "commitment_class_notes_commitment_count",
          title: t("commitmentClass:commitmentCount"),
        },
        {
          id: "commitment_class_notes",
          title: t("common:notesOptional"),
        },
      ],
      rows: data.map(
        (commitmentClass): GenericTableRow => ({
          id: commitmentClass.id!,
          granularity_level: "investor_class",
          cells: {
            commitment_class_name: {
              value: {
                type: "text",
                text: commitmentClass.name,
              },
            },
            commitment_class_custom_fee: {
              value: {
                type: "boolean",
                value: commitmentClass.has_custom_fee,
              },
            },
            commitment_class_custom_waterfall: {
              value: {
                type: "boolean",
                value: commitmentClass.has_custom_waterfall,
              },
            },
            commitment_class_notes_commitment_count: {
              value: {
                type: "text",
                text: commitmentClass.commitment_count.toString(),
              },
            },
            commitment_class_notes: {
              value: {
                type: "text",
                text: commitmentClass.notes,
              },
            },
          },
        }),
      ),
    }),
    [data, t],
  );

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) =>
      row.id
        ? {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .setup({})
              .classes({})
              .edit({ investorClassId: row.id }).$,
          }
        : undefined,
    [fundFamilyId],
  );

  const onAddSuccess = useCallback(async () => {
    await refetch();
    showToast({ message: t("commitmentClass:created"), variant: "success" });
  }, [refetch, showToast, t]);

  const shouldShowBanner = tableData.rows.length <= 1;

  return (
    <PageContent isFullHeight={true}>
      <PageHeader title={t("commitmentClass:classes")} />
      <PageBody>
        {shouldShowBanner && (
          <Alert
            status="info"
            variant="solid"
            mb="16px"
            data-ls-enabled={true}
            data-public={true}
          >
            <AlertIcon>
              <PlusCircleIcon width="24px" height="24px" />
            </AlertIcon>
            <AlertDescription>
              {t("commitmentClass:infoBanner.description")}
            </AlertDescription>
            <Link onClick={openAddDialog} ml={3}>
              {t("commitmentClass:create")}
            </Link>
          </Alert>
        )}
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("commitmentClass:classes")}</TableBoxTitle>
            <DataTableBoxActions>
              {!shouldShowBanner && (
                <Restricted permission={PermissionTypeEnum.config}>
                  <Button size="xs" onClick={openAddDialog}>
                    {t("commitmentClass:createModal.title")}
                  </Button>
                </Restricted>
              )}
            </DataTableBoxActions>
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={tableData} getLinkInfoForRow={getLinkInfoForRow} />
          </TableBoxBody>
        </TableBox>
        {isAddDialogOpen && (
          <CreateCommitmentClassModal
            fundFamilyId={fundFamilyId}
            onCloseComplete={closeAddDialog}
            onSuccess={onAddSuccess}
          />
        )}
      </PageBody>
    </PageContent>
  );
};
